import RemoveRedEyeRoundedIcon from "@mui/icons-material/RemoveRedEyeRounded";
import EditIcon from "@mui/icons-material/Edit";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { CustomAlert, showToastAlert } from "../../admin/components/CustomAlert";
import { allMailOrganizations, getAllCompanies, sendPromoMailOrganization } from "../../services/writerPost";
import { MailModal } from "./mailModal";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { MailOutline } from "@material-ui/icons";

const MailHome = () => {
    const [loaderOpen, setLoaderOpen] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [editMail, setEditMail] = useState(null);
    const [allCompanies, setAllCompanies] = useState([]);
    const token = useSelector((state) => state.token);
    const navigate = useNavigate();

    const createMail = () => {
        showToastAlert("Mail Created", "success");
        setModalOpen(false);
        loadData();
    };
    const updateMail = () => {
        showToastAlert("Mail Updated", "success");
        setModalOpen(false);
        loadData();
    };

    const sendMailToInstitute = async (row) => {
        //ask for confirmation before sending mail
        //message will be all the details of the mail
        const mailDetails = `Are you sure you want to send the following mail?\nName: ${row.name}\nEmail: ${row.email}\nOrganization: ${row.organization}\nType: ${row.type}`;
        if (window.confirm(mailDetails)) {
            // Call the service to send mail
            const response = await sendPromoMailOrganization(token, row._id);
            if (response.status === 200) {
                showToastAlert("Mail sent successfully", "success");
            } else {
                showToastAlert("Failed to send mail", "error");
            }
            //update the records in the table using row._id and response.data.records
            const res = response.data;
            const updatedCompanies = allCompanies.map((company) => {
                if (company._id === row._id) {
                    return { ...company, records: res.records };
                }
                return company;
            });
            setAllCompanies(updatedCompanies);
        }
    };

    const tableColumns = [
        {
            field: "email",
            headerName: "Email",
            flex: 1,
            editable: false,
            renderCell: (params) => (
                params.row.email ? (
                    <span>{params.row.email}</span>
                ) : "-"
            ),
        },
         {
            field: "organization",
            headerName: "Organization",
            flex: 1,
            editable: false,
            renderCell: (params) => (
                params.row.organization ? (
                    <span>{params.row.organization}</span>
                ) : "-"
            ),
        },
         {
            field: "type",
            headerName: "Type",
            flex: 1,
            editable: false,
            renderCell: (params) => (
                params.row.type ? (
                    <span>{params.row.type}</span>
                ) : "-"
            ),
        },
        {
            field: "count",
            headerName: "Mail Count",
            flex: 1,
            editable: false,
            renderCell: (params) => (
                params.row?.records ? (
                    <span>{params.row.records.length}</span>
                ) : "-"
            ),
        },
        {
            field: "sendDate",
            headerName: "Send Date",
            flex: 1,
            editable: false,
            renderCell: (params) => {
                const recs = params?.row?.records;
                if (!Array.isArray(recs) || recs.length === 0) return "-";

                const dateStrings = recs.map((record) => {
                    const sd = record?.sendDate;
                    if (!sd) return null;
                    const d = new Date(sd);
                    if (Number.isNaN(d.getTime())) return null;
                    return d.toLocaleString();
                }).filter(Boolean);

                if (dateStrings.length === 0) return "-";

                return (
                    <div>
                        <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {dateStrings.join(" | ")}
                        </div>
                    </div>
                );
            },
        },
        {
            field: "action",
            headerName: "Action",
            flex: 1,
            minWidth: 230,
            editable: false,
            renderCell: (params) => (

                //add send mail button here
                <Box display="flex" gap={1}>
                    <Tooltip title="Send Mail">
                        <Button
                            color="primary"
                            startIcon={<MailOutline />}
                            onClick={() => { 
                                sendMailToInstitute(params.row); 
                            }}
                        />
                    </Tooltip>
                </Box>
            ),
        },
    ];

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoaderOpen(true);
            const response = await allMailOrganizations();
            setAllCompanies(response.data.mails || []);
            setLoaderOpen(false);
        } catch (err) {
            setLoaderOpen(false);
            showToastAlert("Failed to load mails", "error");
        }
    };

    return (
        <div className="container">
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginTop: "20px"
                }}
            >
                <Box mb={3}>
                    <Button
                        variant="outlined"
                        component={Link}
                        to="/writer"
                        startIcon={<ArrowBackIcon />}
                        sx={{ background: "yellow" }}
                    >
                        Home
                    </Button>
                </Box>
                <h1 className="page-title-1 my-3">Companies</h1>
                <Button onClick={() => { setEditMail(null); setModalOpen(true); }} variant="contained" color="primary">
                    New Mail
                </Button>
            </div>
            <Box
                sx={{
                    width: "100%",
                    height: {
                        xs: 300,
                        sm: 400,
                        md: 500,
                        lg: 600,
                        xl: 800,
                    },
                }}
            >
                <DataGrid
                    rows={allCompanies}
                    columns={tableColumns}
                    pageSize={10}
                    rowsPerPageOptions={[50]}
                    pagination
                    checkboxSelection
                    disableRowSelectionOnClick
                    slots={{ toolbar: GridToolbar }}
                    disableColumnMenu
                    disableColumnSelector
                    getRowId={(row) => row._id}
                    disableColumnFilter={false}
                    componentsProps={{
                        filterPanel: {
                            logicOperators: false,
                        },
                    }}
                />
                <CustomAlert />
            </Box>
            <MailModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                onCreate={createMail}
                onUpdate={updateMail}
                company={editMail}
            />
        </div>
    );
};

export default MailHome;
